import { Radio } from 'antd';
import { useCallback, useMemo } from 'react';
import {
  AiModelState,
  getPrefixName,
  hideRadioClassName,
  Prefix,
  stores2storeScopes,
} from './share';
import { updateFreeShipping } from 'views/BootGuide/service';
import { StoreDescription } from './ShippingDays';
import useSmartField from 'hooks/useSmartField';
import NormalMode from './NormalMode';
import { useTranslate } from '@tolgee/react';

type FV = BootGuide.AiModel.FormValues.FreeShipping;

export function useLabel() {
  const { t } = useTranslate();

  return useMemo(() => {
    return {
      FREE_SHIPPING: t('本店提供包邮服务'),
      NO_FREE_SHIPPING: t('本店不提供包邮服务\n'),
      FREE_SHIPPING_BY_PLATFORM_SEND_COUPON: t('取决于平台发免邮优惠券'),
    } as const;
  }, [t]);
}

function FreeShipping({ prefix, singleMode }: { prefix?: Prefix; singleMode?: boolean }) {
  const label = useLabel();

  const {
    field: { value, onChange },
  } = useSmartField<FV['freeShippingType']>(getPrefixName('freeShippingType', prefix));

  const getHideStyle = (type: FV['freeShippingType']) =>
    singleMode && value !== type ? { display: 'none' } : undefined;

  return (
    <div className="mt-[6px]">
      {Object.keys(label).map((key) => {
        return (
          <div key={key} className="mb-2" style={getHideStyle(key)}>
            <Radio
              className={singleMode ? hideRadioClassName : undefined}
              checked={value === key}
              onChange={({ target: { checked } }) => checked && onChange(key)}
            >
              {label[key]}
            </Radio>
          </div>
        );
      })}
    </div>
  );
}

export function FreeShippingBootGuide(props: AiModelState<FV>) {
  return (
    <NormalMode
      state={props}
      tips={props.t('包邮tips')}
      getFormContent={(prefix) => <FreeShipping prefix={prefix} />}
      getFirstInitialValues={useCallback(({ stores }) => {
        const crossStores = stores.filter(
          ({ storeType, is3pf }) => storeType === 'CROSS' && !is3pf,
        );
        const localStores = stores.filter(
          ({ storeType, is3pf }) => storeType === 'LOCAL' && !is3pf,
        );
        const pfStores = stores.filter(({ is3pf }) => is3pf);

        const values: FV[] = [];

        if (crossStores.length) {
          values.push({
            freeShippingType: 'NO_FREE_SHIPPING',
            storeScopes: stores2storeScopes(crossStores),
          });
        }
        if (localStores.length) {
          values.push({
            freeShippingType: 'FREE_SHIPPING_BY_PLATFORM_SEND_COUPON',
            storeScopes: stores2storeScopes(localStores),
          });
        }
        if (pfStores.length) {
          values.push({
            freeShippingType: 'FREE_SHIPPING_BY_PLATFORM_SEND_COUPON',
            storeScopes: stores2storeScopes(pfStores),
          });
        }

        return { values };
      }, [])}
      updateApi={updateFreeShipping}
      getRemainTips={(remainLength) => props.t('包邮remain_tips', { x: remainLength })}
      descriptionRender={(value, storeMap) => (
        <StoreDescription storeScopes={value} storeMap={storeMap} />
      )}
    />
  );
}

export default FreeShipping;
